import { useEffect } from 'react';

type SeoOptions = {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  image?: string;
  type?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function buildCanonical(path: string) {
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const prefix = basePath ? `/${basePath}` : '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${prefix}${path}`;
}

export function useSeo({ title, description, keywords, path, image, type = 'website', jsonLd }: SeoOptions) {
  useEffect(() => {
    document.title = title;

    setMeta('name', 'description', description);
    if (keywords) {
      setMeta('name', 'keywords', keywords);
    }
    setMeta('name', 'robots', 'index, follow');

    const canonical = buildCanonical(path);
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);

    setMeta('property', 'og:type', type);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    if (image) setMeta('property', 'og:image', image);
    setMeta('property', 'og:site_name', '看料避雷社');
    setMeta('property', 'og:locale', 'zh_CN');

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    if (image) setMeta('name', 'twitter:image', image);
  }, [title, description, keywords, path, image, type]);

  useEffect(() => {
    const scriptId = 'page-jsonld';
    document.getElementById(scriptId)?.remove();
    if (!jsonLd) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
  }, [jsonLd]);
}